import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@osd/ui/card';
import { Col } from '@osd/ui/col';
import { Grid } from '@osd/ui/grid';
import { Row } from '@osd/ui/row';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';
import type { CSSProperties, ReactNode } from 'react';

/**
 * /ui/grid — live docs for the `Grid` + `Row` + `Col` layout primitives.
 *
 * Demonstrates each API option (size, align, cols, spanMd, offsetMd,
 * spanAuto) so the docs surface matches the JS contract — see
 * root/osd#114.
 */
export const Route = createFileRoute('/ui/_layout/grid')({
  component: GridPage,
});

// Card body styled with a min-height so the alignment demos have
// something to align against. Plain `<Card.Body>` would collapse to
// the text height and `align="start" / "center" / "end"` would be
// indistinguishable.
function DemoCard({
  children,
  minHeight = 48,
}: {
  readonly children: ReactNode;
  readonly minHeight?: number;
}) {
  const style: CSSProperties = {
    minHeight: `${minHeight}px`,
    display: 'flex',
    alignItems: 'center',
  };
  return (
    <Card>
      <Card.Body style={style}>
        <Text>{children}</Text>
      </Card.Body>
    </Card>
  );
}

function GridPage() {
  return (
    <Stack gap={6}>
      {/* ── Original demo (preserved) ────────────────────────────── */}
      <Section title="Grid" hint="12-column Bootstrap-style grid — Row + Col">
        <Grid>
          <Row>
            <Col span={12} spanMd={6} spanLg={4}>
              <DemoCard>1/3 width on lg</DemoCard>
            </Col>
            <Col span={12} spanMd={6} spanLg={4}>
              <DemoCard>1/3 width on lg</DemoCard>
            </Col>
            <Col span={12} spanMd={6} spanLg={4}>
              <DemoCard>1/3 width on lg</DemoCard>
            </Col>
          </Row>
        </Grid>
      </Section>

      {/* ── 1. Container sizes (root/osd#114) ───────────────────── */}
      <Section
        title="Container sizes"
        hint="size={'sm' | 'md' | 'lg' | 'xl'} caps max-width; default 'fluid' is full-width"
      >
        <Stack gap={4}>
          <Grid size="sm">
            <Row>
              <Col span={12}>
                <DemoCard>size=&quot;sm&quot; — capped at sm breakpoint max-width</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid size="md">
            <Row>
              <Col span={12}>
                <DemoCard>size=&quot;md&quot; — capped at md breakpoint max-width</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid size="lg">
            <Row>
              <Col span={12}>
                <DemoCard>size=&quot;lg&quot; — capped at lg breakpoint max-width</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid size="xl">
            <Row>
              <Col span={12}>
                <DemoCard>size=&quot;xl&quot; — capped at xl breakpoint max-width</DemoCard>
              </Col>
            </Row>
          </Grid>
        </Stack>
      </Section>

      {/* ── 2. Row alignment (root/osd#114) ─────────────────────── */}
      <Section
        title="Row alignment"
        hint="align controls vertical (cross-axis) alignment of cols; default 'stretch' equals heights"
      >
        <Stack gap={4}>
          <Grid>
            <Row align="start">
              <Col span={6}>
                <DemoCard minHeight={120}>align=&quot;start&quot;</DemoCard>
              </Col>
              <Col span={6}>
                <DemoCard>Tall left card aligns to top of row</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row align="center">
              <Col span={6}>
                <DemoCard minHeight={120}>align=&quot;center&quot;</DemoCard>
              </Col>
              <Col span={6}>
                <DemoCard>Centered against the tall left card</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row align="end">
              <Col span={6}>
                <DemoCard minHeight={120}>align=&quot;end&quot;</DemoCard>
              </Col>
              <Col span={6}>
                <DemoCard>Pinned to bottom of the tall left card</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row>
              <Col span={6}>
                <DemoCard minHeight={120}>default (stretch) — sibling grows to match</DemoCard>
              </Col>
              <Col span={6}>
                <DemoCard>Short content, but stretched</DemoCard>
              </Col>
            </Row>
          </Grid>
        </Stack>
      </Section>

      {/* ── 3. Row cols preset (root/osd#114) ───────────────────── */}
      <Section
        title="Row cols preset"
        hint="cols={'2'|'3'|'4'|'5'|'6'} sets every direct child to that fraction without per-Col span"
      >
        <Stack gap={4}>
          <Grid>
            <Row cols={3}>
              <Col>
                <DemoCard>Row cols=3 — 1/3 each</DemoCard>
              </Col>
              <Col>
                <DemoCard>1/3</DemoCard>
              </Col>
              <Col>
                <DemoCard>1/3</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row cols={4} colsMd={2}>
              <Col>
                <DemoCard>cols=4 colsMd=2</DemoCard>
              </Col>
              <Col>
                <DemoCard>4 across on xl</DemoCard>
              </Col>
              <Col>
                <DemoCard>2 across on md</DemoCard>
              </Col>
              <Col>
                <DemoCard>stacks on sm</DemoCard>
              </Col>
            </Row>
          </Grid>
        </Stack>
      </Section>

      {/* ── 4. Column offsets (root/osd#114) ───────────────────── */}
      <Section
        title="Column offsets"
        hint="offsetMd / offsetLg / etc. push a col right by N column-widths — most-missed feature"
      >
        <Stack gap={4}>
          <Grid>
            <Row>
              <Col span={6} offsetMd={3}>
                <DemoCard>span=6 offsetMd=3 — pushes right by 3 cols at md+</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row>
              <Col span={4} offsetLg={2}>
                <DemoCard>offsetLg=2</DemoCard>
              </Col>
              <Col span={4} offsetLg={2}>
                <DemoCard>offsetLg=2</DemoCard>
              </Col>
            </Row>
          </Grid>
        </Stack>
      </Section>

      {/* ── 5. Auto-width column (root/osd#114) ────────────────── */}
      <Section
        title="Auto-width column"
        hint="spanAuto hugs content; siblings stay at their fixed fraction"
      >
        <Stack gap={4}>
          <Grid>
            <Row>
              <Col spanAuto>
                <DemoCard minHeight={48}>spanAuto — content-sized</DemoCard>
              </Col>
              <Col span={6}>
                <DemoCard>span=6 — fixed half</DemoCard>
              </Col>
            </Row>
          </Grid>
          <Grid>
            <Row>
              <Col spanAuto>
                <DemoCard>auto</DemoCard>
              </Col>
              <Col span={8}>
                <DemoCard>span=8</DemoCard>
              </Col>
              <Col spanAuto>
                <DemoCard>auto</DemoCard>
              </Col>
            </Row>
          </Grid>
        </Stack>
      </Section>

      {/* ── Code reference (expanded) ─────────────────────────── */}
      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Three equal columns"
            language="tsx"
            code={`<Grid>
  <Row>
    <Col span={12} spanMd={6} spanLg={4}>
      <Card><Card.Body>First</Card.Body></Card>
    </Col>
    <Col span={12} spanMd={6} spanLg={4}>
      <Card><Card.Body>Second</Card.Body></Card>
    </Col>
    <Col span={12} spanMd={6} spanLg={4}>
      <Card><Card.Body>Third</Card.Body></Card>
    </Col>
  </Row>
</Grid>`}
          />

          <CodeBlock
            title="Two-thirds + one-third"
            language="tsx"
            code={`<Grid>
  <Row>
    <Col span={12} spanLg={8}>
      <Card><Card.Body>Main</Card.Body></Card>
    </Col>
    <Col span={12} spanLg={4}>
      <Card><Card.Body>Sidebar</Card.Body></Card>
    </Col>
  </Row>
</Grid>`}
          />

          <CodeBlock
            title="Container sizes"
            language="tsx"
            code={`<Grid size="sm">…capped content…</Grid>
<Grid size="md">…capped content…</Grid>
<Grid size="lg">…capped content…</Grid>
<Grid size="xl">…capped content…</Grid>`}
          />

          <CodeBlock
            title="Row alignment"
            language="tsx"
            code={`<Row align="start">   <Col>…</Col> <Col>…</Col> </Row>
<Row align="center">  <Col>…</Col> <Col>…</Col> </Row>
<Row align="end">     <Col>…</Col> <Col>…</Col> </Row>
<Row>                 <Col>…</Col> <Col>…</Col> </Row>  {/* stretch */}`}
          />

          <CodeBlock
            title="Row cols preset (responsive)"
            language="tsx"
            code={`<Row cols={3}>
              <Col>1/3 each</Col><Col>1/3</Col><Col>1/3</Col>
            </Row>
            <Row cols={4} colsMd={2}>
              <Col>4 across on xl</Col><Col>…</Col><Col>…</Col><Col>…</Col>
            </Row>`}
          />

          <CodeBlock
            title="Column offsets"
            language="tsx"
            code={`<Row>
  <Col span={6} offsetMd={3}>pushed right by 3 cols at md+</Col>
</Row>`}
          />

          <CodeBlock
            title="Auto-width column"
            language="tsx"
            code={`<Row>
  <Col spanAuto>content-sized</Col>
  <Col span={6}>fixed half</Col>
</Row>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}